import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import moment from 'moment';

import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
  matchPath,
} from 'react-router-dom';
import {
  Layout,
  Spin,
  Row,
  Col,
  Drawer,
  Menu,
  DatePicker as AntDatePicker,
} from 'antd';
import {
  MenuOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  FundOutlined,
  CalendarTwoTone,
  GlobalOutlined,
} from '@ant-design/icons';

import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import locale from 'antd/es/date-picker/locale/fr_FR';

import HomeView from '../views/HomeView';
import AboutView from '../views/AboutView';
import MixView from '../views/MixView';
import PlantView from '../views/PlantView';
import USNPPView from '../views/USNPPView';

import PlantMap from './PlantMap';
import USNPPMap from './USNPPMap';

import buildLoader from '../HOC/buildLoader';
import { loadAllPlants, plantsLoadedSelector } from '../store/plants';

import { loadAllReactors, reactorsLoadedSelector } from '../store/reactors';
import {
  loadAllProductions,
  productionsLoadedSelector,
  productionsErrorSelector,
} from '../store/productions';
import { loadAllMix, mixLoadedSelector, mixErrorSelector } from '../store/mix';

import {
  HEADER_HEIGHT,
  DRAWER_WIDTH,
  DRAWER_WIDTH_SMALL,
  getWindowHeight,
} from '../utils';
import {
  loadAllUnavailabilities,
  unavailabilitiesLoadedSelector,
  unavailabilitiesErrorSelector,
} from '../store/unavailabilities';
import { loadAllRivers, riversLoadedSelector } from '../store/rivers';
import { loadAllUsNPPs, usNPPsLoadedSelector } from '../store/usNPPs';
import { getCurrentDate } from '../store/otherSelectors';

dayjs.locale('fr');

const PlantsLoader = buildLoader(loadAllPlants);
const ReactorsLoader = buildLoader(loadAllReactors);
const ProductionsLoader = buildLoader(loadAllProductions);
const MixLoader = buildLoader(loadAllMix);
const UnavailabilitiesLoader = buildLoader(loadAllUnavailabilities);
const RiversLoader = buildLoader(loadAllRivers);
const UsNPPsLoader = buildLoader(loadAllUsNPPs);

function AppLayout({
  isLoaded,
  error = null,
  goTo,
  currentLocation,
  currentDate,
  isFullPage,
  currentPlantId = null,
}) {
  // Use the same breakpoint as CSS (750px) to avoid inconsistencies
  const isSmallScreen = window.innerWidth <= 1500;
  const drawerHeight = isFullPage ? getWindowHeight() - HEADER_HEIGHT : 220;
  const drawerWidth = isSmallScreen
    ? Math.floor(window.innerWidth * 0.5)
    : Math.min(DRAWER_WIDTH, 600); // Use 50% width on small screens, max 600px on large screens

  // Debug logging
  console.log('AppLayout debug:', {
    isSmallScreen,
    drawerWidth,
    windowWidth: window.innerWidth,
  });

  // moment for app logic/formatting
  const m = useMemo(() => moment(currentDate), [currentDate]);
  // dayjs for AntD DatePicker (v5)
  const djs = useMemo(() => dayjs(m.toDate()), [m]);

  const handleDateChange = v => {
    if (!v) return;
    const next = moment(v.toDate());
    if (next.isSame(moment(), 'day')) {
      goTo(`${currentLocation}`, false);
    } else {
      goTo(`${currentLocation}?date=${next.toISOString()}`, false);
    }
  };

  const menuItems = useMemo(
    () => [
      { key: '/home', icon: <HomeOutlined />, label: 'Accueil' },
      { key: '/mix', icon: <FundOutlined />, label: 'Mix' },
      { key: '/us-npps', icon: <GlobalOutlined />, label: 'US NPPs' },
      { key: '/about', icon: <InfoCircleOutlined />, label: 'À propos' },
    ],
    [],
  );

  const selectedKey = useMemo(() => {
    if (currentLocation.startsWith('/mix')) return '/mix';
    if (currentLocation.startsWith('/us-npp')) return '/us-npps';
    if (currentLocation.startsWith('/about')) return '/about';
    return '/home';
  }, [currentLocation]);

  return (
    <div className="AppLayout">
      <PlantsLoader />
      <ReactorsLoader />
      <RiversLoader />
      <UsNPPsLoader />

      <ProductionsLoader date={currentDate} />
      <MixLoader date={moment(currentDate).format('YYYY-MM-DD')} />
      <UnavailabilitiesLoader date={currentDate} />

      <Spin
        size="large"
        spinning={!isLoaded}
        indicator={
          <div>
            <img
              className="AppLayout__loadingIcon"
              src="loading_icon.svg"
              alt=""
            />
            {error && (
              <div className="AppLayout__errorMessage">
                Une erreur est survenue, veuillez essayer dans quelques minutes
              </div>
            )}
          </div>
        }
      >
        {!isLoaded && <div className="AppLayout__splash" />}
        {isLoaded && (
          <Layout>
            <Drawer
              title={null}
              open
              placement="left" // Always use left placement for side-by-side layout
              style={{
                boxShadow: 'none', // Remove box shadow to eliminate visual gap
              }}
              mask={false}
              closable={false}
              width={drawerWidth} // Use responsive width
              height={drawerHeight}
              styles={{
                header: { display: 'none' },
                body: {
                  padding: 0,
                  paddingBottom: 16,
                  overflowY: 'auto',
                  maxHeight: 'calc(100vh - 64px)',
                },
                content: {
                  background: '#fff',
                  borderRadius: '0 8px 8px 0',
                },
              }}
              resizable={false} // Disable resize to prevent width inconsistencies
              collapsible={false} // Disable collapse to maintain consistent behavior
            >
              <Routes>
                <Route path="/home" element={<HomeView />} />
                <Route path="/mix" element={<MixView />} />
                <Route path="/about" element={<AboutView />} />
                <Route path="/plant/:plantId/*" element={<PlantView />} />
                <Route path="/us-npp/:usNPPId/*" element={<USNPPView />} />
                <Route path="/us-npps" element={<USNPPView />} />
                <Route path="*" element={<Navigate to="/home" replace />} />
              </Routes>
            </Drawer>

            <Row>
              <Col span={24}>
                <Layout.Header className="AppLayout__header">
                  <Menu
                    theme="dark"
                    mode="horizontal"
                    className="AppLayout__menu"
                    style={{ minWidth: 0, flex: '1 1 auto' }}
                    selectedKeys={[selectedKey]}
                    onClick={({ key }) => goTo(key)}
                    overflowedIndicator={<MenuOutlined />} // hamburger instead of ellipsis
                    items={menuItems}
                  />

                  {/* Visible, stable AntD DatePicker (dayjs in / moment out) */}
                  <AntDatePicker
                    value={djs}
                    onChange={handleDateChange}
                    locale={locale}
                    allowClear={false}
                    inputReadOnly
                    showTime={{ format: 'HH:00' }}
                    maxDate={dayjs(new Date())}
                    format={date =>
                      isSmallScreen
                        ? '' // Show no text on small screens
                        : `${date.format('dddd DD/MM/YYYY')} à ${date.format(
                            'HH',
                          )}:00`
                    }
                    prefix={<CalendarTwoTone twoToneColor="#fff" />}
                    suffixIcon={null}
                    style={{
                      backgroundColor: '#1089FF',
                      color: '#fff',
                      borderRadius: 0,
                      border: 'none',
                      width: isSmallScreen ? '40px' : 'auto', // Make it smaller on small screens
                      minWidth: isSmallScreen ? '40px' : 'auto',
                    }}
                    getPopupContainer={() => document.body}
                    className="AppLayout__datePicker"
                  />
                </Layout.Header>

                <Layout.Content className="AppLayout__content">
                  {currentLocation.startsWith('/us-npp') ? (
                    <USNPPMap
                      currentDate={currentDate}
                      currentUSNPPId={currentPlantId}
                      onUSNPPClick={usNPP => goTo(`/us-npp/${usNPP.id}`)}
                      drawerHeight={drawerHeight}
                    />
                  ) : (
                    <PlantMap
                      currentDate={currentDate}
                      currentPlantId={currentPlantId}
                      onPlantClick={plant => goTo(`/plant/${plant.id}`)}
                      drawerHeight={drawerHeight}
                    />
                  )}
                </Layout.Content>
              </Col>
            </Row>
          </Layout>
        )}
      </Spin>
    </div>
  );
}

AppLayout.propTypes = {
  isLoaded: PropTypes.bool.isRequired,
  error: PropTypes.object,
  goTo: PropTypes.func.isRequired,
  currentLocation: PropTypes.string.isRequired,
  currentDate: PropTypes.string.isRequired,
  isFullPage: PropTypes.bool.isRequired,
  currentPlantId: PropTypes.string,
};

// withRouter needed to prevent blocking
function AppLayoutWithRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  const matchPlantPath = matchPath('/plant/:id/*', location.pathname);
  const currentDateRaw = getCurrentDate(location);
  const currentDate = moment(currentDateRaw)//.format('YYYY-MM-DD');

  const mixError = useSelector(state =>
    mixErrorSelector({ date: currentDate }, state),
  );
  const prodError = useSelector(state =>
    productionsErrorSelector({ date: currentDate }, state),
  );
  const unavailabilitiesError = useSelector(state =>
    unavailabilitiesErrorSelector({ date: currentDate }, state),
  );

  const isLoaded = useSelector(
    state =>
      (plantsLoadedSelector(state) &&
        reactorsLoadedSelector(state) &&
        productionsLoadedSelector({ date: currentDate }, state) &&
        mixLoadedSelector({ date: currentDate }, state) &&
        unavailabilitiesLoadedSelector({ date: currentDate }, state) &&
        riversLoadedSelector(state) &&
        usNPPsLoadedSelector(state)) ||
      false,
  );

  const error = prodError || unavailabilitiesError || mixError;

  const goTo = (url, withSearch = true) =>
    navigate(`${url}${withSearch ? location.search : ''}`);

  const currentLocation = location.pathname;
  const currentPlantId = matchPlantPath && matchPlantPath.params.id;

  const isFullPage =
    !!matchPath('/plant/:id/:reactorIndex', location.pathname) ||
    !!matchPath('/us-npp/:id', location.pathname) ||
    !!matchPath('/us-npps', location.pathname) ||
    !!matchPath('/mix', location.pathname) ||
    !!matchPath('/about', location.pathname);

  return (
    <AppLayout
      isLoaded={isLoaded}
      error={error}
      goTo={goTo}
      currentLocation={currentLocation}
      currentDate={currentDate}
      isFullPage={isFullPage}
      currentPlantId={currentPlantId}
    />
  );
}

export default AppLayoutWithRouter;
